import type { ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router";
import { useTranslation } from "react-i18next";
import { useAuth } from "./auth/AuthContext";
import { Dismissed } from "./pages/Dismissed";
import { Feed } from "./pages/Feed";
import { Login } from "./pages/Login";
import { Saved } from "./pages/Saved";
import { Settings } from "./pages/Settings";

// Everything except the login screen needs a session. When any request comes back 401
// the auth state flips to anonymous and this sends the person to the login screen.
function RequireAuth({ children }: { children: ReactNode }) {
  const { state } = useAuth();
  const { t } = useTranslation();
  if (state === "loading") return <p className="status">{t("loading")}</p>;
  if (state === "anonymous") return <Navigate to="/login" replace />;
  return children;
}

function LoginRoute() {
  const { state } = useAuth();
  const { t } = useTranslation();
  if (state === "loading") return <p className="status">{t("loading")}</p>;
  if (state === "authenticated") return <Navigate to="/" replace />;
  return <Login />;
}

const guarded = (element: ReactNode) => <RequireAuth>{element}</RequireAuth>;

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/" element={guarded(<Feed />)} />
      <Route path="/saved" element={guarded(<Saved />)} />
      <Route path="/dismissed" element={guarded(<Dismissed />)} />
      <Route path="/settings" element={guarded(<Settings />)} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
